/**
 * Vuex store for shell state only: Sanctum token, current admin, dark mode, errors.
 * Persisted under secure-ls key `webwalker`. Finance records live in billing’s DB via API.
 */
import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import SecureLS from 'secure-ls';
import AuthApi from '@/api/AuthApi';
import SettingsApi from '@/api/SettingsApi';

// Use Secure (encrypted) Local Storage for storing data in the browser
const ls = new SecureLS({
  encodingType: 'aes',
  isCompression: false,
  encryptionSecret: process.env.VUE_APP_LS_ENCRYPTION_SECRET,
});

const store = createStore({
  plugins: [
    createPersistedState({
      key: 'webwalker',
      storage: {
        getItem: (key) => {
          try {
            return ls.get(key);
          } catch (err) {
            ls.clear();
            return ls.get(key);
          }
        },
        setItem: (key, value) => ls.set(key, value),
        removeItem: (key) => ls.remove(key),
      },
    }),
  ],
  state: {
    token: false, // Billing admin Sanctum token
    admin: false, // Current admin details
    isDarkMode: false, // Dark\Light theme
    errors: [], // List of errors
  },
  getters: {
    authenticated(state) {
      if (state.token) {
        return true;
      }
      return false;
    },
  },
  mutations: {
    setToken(state, token) {
      state.token = token;
    },
    setAdmin(state, admin) {
      state.admin = admin;
    },
    setDarkMode(state, isDarkMode) {
      state.isDarkMode = isDarkMode;
    },
    pushErrors(state, errors) {
      state.errors.push(errors);
    },
    clearErrors(state) {
      state.errors = [];
    },
  },
  actions: {
    loadAdmin({ commit }) {
      return AuthApi.auth().then((response) => {
        commit('setAdmin', response.data.data);

        if (response.data.data.settings) {
          commit('setDarkMode', response.data.data.settings.dark_mode);
        }
      }).catch((error) => {
        if (error.response && error.response.data && error.response.data.errors) {
          commit('pushErrors', error.response.data.errors);
        }
        localStorage.clear();
      });
    },
    switchDarkMode({ commit }, isDarkMode) {
      SettingsApi.updateAdmin({
        settings: { dark_mode: isDarkMode },
      });

      commit('setDarkMode', isDarkMode);
    },
    pushErrors({ commit }, errors) {
      commit('pushErrors', errors);
    },
    clearErrors({ commit, state }) {
      if (state.errors.length) {
        commit('clearErrors');
      }
    },
  },
});

export default store;
